/**
 * Color mapping for bubble rendering — steampunk clock tower palette.
 * All colors meet WCAG AA contrast (4.5:1) against dark background #1a1510.
 */

import { BubbleColor } from '../game/types.js';

export interface BubbleColorInfo {
  /** Hex color value for tinting */
  readonly hex: number;
  /** CSS hex string */
  readonly css: string;
  /** Colorblind-friendly symbol (engraved rune style) drawn on the gear */
  readonly symbol: string;
  /** Human-readable name */
  readonly name: string;
}

/**
 * Steampunk color map — warm metallics and mineral tones.
 * Contrast ratios verified against #1a1510 background (WCAG AA >= 4.5:1).
 */
export const BUBBLE_COLORS: Readonly<Record<BubbleColor, BubbleColorInfo>> = {
  [BubbleColor.Red]:    { hex: 0xd03030, css: '#d03030', symbol: '●', name: 'Copper' },
  [BubbleColor.Blue]:   { hex: 0x4a8fa8, css: '#4a8fa8', symbol: '▲', name: 'Oxidized' },
  [BubbleColor.Green]:  { hex: 0x6b8f4a, css: '#6b8f4a', symbol: '■', name: 'Patina' },
  [BubbleColor.Yellow]: { hex: 0xe8c840, css: '#e8c840', symbol: '◆', name: 'Brass' },
  [BubbleColor.Purple]: { hex: 0x8b6aad, css: '#8b6aad', symbol: '★', name: 'Amethyst' },
  [BubbleColor.Orange]: { hex: 0xd48828, css: '#d48828', symbol: '✦', name: 'Forge' },
};

/** Wild bubble indicator color (crystal artifact glow) */
export const WILD_COLOR = 0xe8dcc8;

/** Bomb bubble indicator color (black powder keg) */
export const BOMB_COLOR = 0xcc3322;

/** Obstacle bubble color (riveted iron plate) */
export const OBSTACLE_COLOR = 0x555566;

/** Primary accent color — polished brass (used across UI) */
export const ACCENT_BRASS = 0xc49a3c;
export const ACCENT_BRASS_CSS = '#c49a3c';

/** Secondary background surface — dark iron */
export const SURFACE_IRON_CSS = '#2a2218';

/** Warm parchment text color */
export const PARCHMENT_CSS = '#e8dcc8';

/** Get the hex color for a BubbleColor enum value */
export function getColorHex(color: BubbleColor): number {
  return BUBBLE_COLORS[color].hex;
}

/** Get the colorblind symbol for a BubbleColor enum value */
export function getColorSymbol(color: BubbleColor): string {
  return BUBBLE_COLORS[color].symbol;
}

/**
 * Returns a background color that shifts from warm soot (lower floors)
 * to cold midnight iron (upper reaches) as the player climbs.
 *
 * Start: #1a1510 (warm soot — copper/amber lower floors)
 * End:   #0d1520 (midnight iron — cold upper reaches)
 */
export function getBackgroundColor(turnNumber: number): number {
  const t = Math.min(turnNumber / 100, 1);

  // Start: (26, 21, 16) → End: (13, 21, 32) — warm to cold shift
  const r = Math.round(26 - 13 * t);
  const g = Math.round(21);
  const b = Math.round(16 + 16 * t);

  return (r << 16) | (g << 8) | b;
}
